// Indexing tab — the admin's window into the content-search pipeline.
//
// Speaks the backend's DB status vocabulary (pending / extracting / indexed /
// failed / unsupported / too_large) plus the derived buckets `missing` (bytes,
// no row) and `stuck` (extracting longer than any run can take) — NOT the
// five-word wire vocabulary the listing badges use, because an admin
// diagnosing the pipeline needs unsupported vs too_large to stay distinct.
//
// Three layers, top to bottom: health (the pipeline's dependencies), the
// doctor (what is inconsistent, with a fix per line), and the ledger (chips
// as filters, a paged table, a detail drawer per row). Actions: Retry (small
// sets, per row or per bucket) and Rebuild (a job over a scope with
// progress and cancel — see services/jobs.py). Retry is safe to spam by
// construction: the worker short-circuits on unchanged checksums; Rebuild
// with `force` deliberately is not, which is why it confirms first.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Activity, AlertTriangle, CheckCircle2, ChevronDown, Copy, Loader2, RefreshCw, RotateCw, ScanSearch,
  Search as SearchIcon, Stethoscope, Wrench, XCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { api, ep } from '../lib/api.js';
import { usePagedList } from '../lib/usePagedList.js';
import { ConfirmDialog } from './ConfirmDialog.jsx';
import { ListPagination } from './ListPagination.jsx';

const POLL_MS = 5000;

// Chip styling per DB status. Order is display order.
const STATUSES = [
  { value: 'indexed', chip: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' },
  { value: 'pending', chip: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 border-amber-200 dark:border-amber-800' },
  { value: 'extracting', chip: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 border-blue-200 dark:border-blue-800' },
  { value: 'stuck', chip: 'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 border-orange-200 dark:border-orange-800' },
  { value: 'failed', chip: 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800' },
  { value: 'missing', chip: 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-violet-200 dark:border-violet-800' },
  { value: 'unsupported', chip: 'bg-muted/50 text-muted-foreground border-border' },
  { value: 'too_large', chip: 'bg-muted/50 text-muted-foreground border-border' },
];
const CHIP_BY_STATUS = Object.fromEntries(STATUSES.map((s) => [s.value, s.chip]));
const EMBED_STATUSES = ['embedded', 'pending', 'failed', 'skipped', 'not_attempted'];

// Statuses where a retry can actually change the outcome.
const RETRYABLE = new Set(['failed', 'missing', 'stuck']);
// Rebuild scopes, display order; `force` is offered on the ones where it
// changes anything (stuck/outdated/embeddings imply it server-side).
const SCOPES = ['failed', 'missing', 'stuck', 'outdated', 'embeddings', 'all'];
const FORCEABLE = new Set(['failed', 'missing', 'all', 'embeddings']);

function StatusChip({ status, t }) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium whitespace-nowrap',
        CHIP_BY_STATUS[status] || 'bg-muted/50 text-muted-foreground border-border',
      )}
    >
      {t(`indexing.status.${status}`)}
    </span>
  );
}

function fmt(value) {
  return value ? moment(value).format('DD MMM YYYY HH:mm') : '—';
}

function elapsed(job) {
  if (!job?.started_at) return null;
  const end = job.finished_at ? moment(job.finished_at) : moment();
  const secs = Math.max(0, end.diff(moment(job.started_at), 'seconds'));
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

// ------------------------------------------------------------- health

function HealthStrip({ health, t }) {
  if (!health) return null;
  const probes = ['broker', 'worker', 'tika', 'embeddings', 'qdrant', 'storage'];
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="health-strip">
      <Activity className="h-4 w-4 text-muted-foreground" />
      {probes.map((key) => {
        const p = health[key];
        if (!p) return null;
        return (
          <span
            key={key}
            title={`${p.detail || ''}${p.latency_ms != null ? ` · ${p.latency_ms} ms` : ''}`}
            className={cn(
              'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs',
              p.ok ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800' : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800',
            )}
          >
            {p.ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
            {t(`indexing.health.${key}`)}
          </span>
        );
      })}
    </div>
  );
}

// ------------------------------------------------------------- doctor

function DoctorPanel({ report, onFix, busy, t }) {
  if (!report) return null;
  const problems = report.checks.filter((c) => c.severity !== 'ok');
  return (
    <div className="rounded-lg border bg-card p-3" data-testid="doctor-panel">
      <div className="flex items-center gap-2 text-sm font-medium text-foreground">
        <Stethoscope className="h-4 w-4 text-muted-foreground" />
        {t('indexing.doctor.title')}
        <span className="ml-auto text-xs font-normal text-muted-foreground">{fmt(report.generated_at)}</span>
      </div>
      {problems.length === 0 ? (
        <p className="mt-2 flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-300">
          <CheckCircle2 className="h-4 w-4" /> {t('indexing.doctor.allGood')}
        </p>
      ) : (
        <ul className="mt-2 divide-y">
          {problems.map((c) => (
            <li key={c.key} className="flex flex-wrap items-center gap-2 py-1.5 text-sm">
              {c.severity === 'error' ? (
                <XCircle className="h-4 w-4 flex-none text-red-500 dark:text-red-400" />
              ) : c.severity === 'warn' ? (
                <AlertTriangle className="h-4 w-4 flex-none text-amber-500 dark:text-amber-400" />
              ) : (
                <span className="inline-block h-4 w-4 flex-none rounded-full bg-muted" />
              )}
              <span className="font-medium">{t(`indexing.doctor.checks.${c.key}`)}</span>
              {c.count != null && <span className="rounded bg-muted px-1.5 text-xs font-mono">{c.count}</span>}
              {c.detail && <span className="text-xs text-muted-foreground">{c.detail}</span>}
              {c.fix && (
                <Button size="sm" variant="outline" className="ml-auto h-7 gap-1" disabled={busy} onClick={() => onFix(c)} data-testid={`fix-${c.key}`}>
                  <Wrench className="h-3 w-3" />
                  {c.fix.startsWith('cleanup:') ? t('indexing.doctor.cleanup') : t('indexing.doctor.rebuild', { scope: t(`indexing.scopes.${c.fix}`) })}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- job

function JobCard({ job, workerOk, onCancel, t }) {
  if (!job) return null;
  const denominator = job.feeding_done ? job.published : Math.max(job.total, job.published);
  const pct = denominator ? Math.min(100, Math.round((job.settled / denominator) * 100)) : 0;
  const active = job.status === 'queued' || job.status === 'running';
  return (
    <div className="rounded-lg border bg-card p-3" data-testid="job-card">
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {active ? <Loader2 className="h-4 w-4 animate-spin text-blue-500 dark:text-blue-400" /> : job.status === 'done' ? <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" /> : <XCircle className="h-4 w-4 text-muted-foreground" />}
        <span className="font-medium">
          {t('indexing.job.title', { scope: t(`indexing.scopes.${job.scope}`) })}
          {job.force && <span className="ml-1 text-xs text-amber-700 dark:text-amber-300">({t('indexing.job.forced')})</span>}
        </span>
        <span className={cn('rounded-full border px-2 py-0.5 text-xs', active ? 'border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300' : 'border-border text-muted-foreground')}>
          {t(`indexing.job.status.${job.status}`)}
        </span>
        {job.status === 'queued' && !workerOk && (
          <span className="text-xs text-amber-700 dark:text-amber-300">{t('indexing.job.waitingForWorker')}</span>
        )}
        <span className="ml-auto text-xs text-muted-foreground">
          {job.settled} / {denominator} · {t('indexing.job.done')} {job.done} · {t('indexing.job.failed')} {job.failed} · {t('indexing.job.skipped')} {job.skipped}
          {elapsed(job) && ` · ${elapsed(job)}`}
        </span>
        {active && (
          <Button size="sm" variant="outline" className="h-7" onClick={onCancel} data-testid="job-cancel">
            {t('indexing.job.cancel')}
          </Button>
        )}
      </div>
      <Progress value={pct} className="mt-2 h-2" />
      {job.error && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{job.error}</p>}
    </div>
  );
}

function RecentJobs({ jobs, t }) {
  if (!jobs || jobs.length === 0) return null;
  return (
    <details className="rounded-lg border bg-card px-3 py-2 text-sm" data-testid="recent-jobs">
      <summary className="cursor-pointer text-muted-foreground">{t('indexing.jobs.recent', { count: jobs.length })}</summary>
      <ul className="mt-2 divide-y">
        {jobs.map((job) => (
          <li key={job.id} className="flex flex-wrap items-center gap-2 py-1.5 text-xs">
            <span className="font-mono text-muted-foreground">#{job.id}</span>
            <span className="font-medium">{t(`indexing.scopes.${job.scope}`)}</span>
            {job.force && <span className="text-amber-700 dark:text-amber-300">({t('indexing.job.forced')})</span>}
            <span className={cn('rounded-full border px-2 py-0.5', job.status === 'done' ? 'border-emerald-200 dark:border-emerald-800 text-emerald-700 dark:text-emerald-300' : job.status === 'failed' ? 'border-red-200 dark:border-red-800 text-red-700 dark:text-red-300' : 'border-border text-muted-foreground')}>
              {t(`indexing.job.status.${job.status}`)}
            </span>
            <span className="text-muted-foreground">
              {job.settled} / {job.feeding_done ? job.published : Math.max(job.total, job.published)} · {t('indexing.job.failed')} {job.failed} · {t('indexing.job.skipped')} {job.skipped}
              {elapsed(job) && ` · ${elapsed(job)}`}
            </span>
            <span className="ml-auto text-muted-foreground">{fmt(job.finished_at || job.started_at || job.created_at)}</span>
            {job.error && <span className="w-full truncate text-red-600 dark:text-red-400" title={job.error}>{job.error}</span>}
          </li>
        ))}
      </ul>
    </details>
  );
}

// ------------------------------------------------------------- drawer

function RowDrawer({ row, onClose, onRetry, t }) {
  const copy = (text) => navigator.clipboard?.writeText(String(text)).then(() => toast.success(t('indexing.drawer.copied')));
  return (
    <Sheet open={Boolean(row)} onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent className="w-[420px] sm:max-w-[480px] overflow-y-auto">
        {row && (
          <>
            <SheetHeader>
              <SheetTitle className="break-all">{row.name}</SheetTitle>
              <SheetDescription className="flex items-center gap-2">
                <StatusChip status={row.status} t={t} />
                <button type="button" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" onClick={() => copy(row.file_id)}>
                  <Copy className="h-3 w-3" /> #{row.file_id}
                </button>
              </SheetDescription>
            </SheetHeader>
            <dl className="mt-4 space-y-2 text-sm">
              {row.error && (
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">{t('indexing.drawer.error')}</dt>
                  <dd className="whitespace-pre-wrap break-all rounded bg-red-50 dark:bg-red-900/20 p-2 font-mono text-xs text-red-700 dark:text-red-300">{row.error}</dd>
                </div>
              )}
              {row.embed_error && (
                <div>
                  <dt className="text-xs uppercase text-muted-foreground">{t('indexing.drawer.embedError')}</dt>
                  <dd className="whitespace-pre-wrap break-all rounded bg-amber-50 dark:bg-amber-900/20 p-2 font-mono text-xs text-amber-700 dark:text-amber-300">{row.embed_error}</dd>
                </div>
              )}
              {[
                ['extractor', row.extractor], ['lang', row.lang], ['embedStatus', row.embed_status],
                ['attempts', row.attempts], ['lastErrorAt', fmt(row.last_error_at)],
                ['indexedAt', fmt(row.indexed_at)], ['updatedAt', fmt(row.updated_at)],
              ].map(([key, value]) => (
                <div key={key} className="flex justify-between gap-3">
                  <dt className="text-muted-foreground">{t(`indexing.drawer.${key}`)}</dt>
                  <dd className="text-right">{value ?? '—'}</dd>
                </div>
              ))}
              <div>
                <dt className="text-muted-foreground">{t('indexing.drawer.checksum')}</dt>
                <dd className="font-mono text-xs break-all">
                  {row.checksum || '—'}
                  {row.current_checksum && row.checksum !== row.current_checksum && (
                    <span className="ml-1 rounded bg-amber-50 dark:bg-amber-900/20 px-1 text-amber-700 dark:text-amber-300">{t('indexing.drawer.outdated')}</span>
                  )}
                </dd>
              </div>
            </dl>
            <div className="mt-4 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => onRetry(row, false)}>{t('indexing.retry')}</Button>
              <Button size="sm" variant="outline" onClick={() => onRetry(row, true)}>{t('indexing.drawer.forceRetry')}</Button>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ---------------------------------------------------------------- tab

export const IndexingTab = ({ projectId }) => {
  const { t } = useTranslation('console');
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [doctor, setDoctor] = useState(null);
  const [doctorLoading, setDoctorLoading] = useState(false);
  const [recentJobs, setRecentJobs] = useState([]);
  const [busy, setBusy] = useState(null); // 'all' | 'selected' | file_id | 'rebuild' | 'cleanup'
  const [selected, setSelected] = useState(() => new Set());
  const [drawerRow, setDrawerRow] = useState(null);
  const [confirm, setConfirm] = useState(null); // {kind:'rebuild'|'retryAll'|'cleanup', scope, force, count}
  const activeRef = useRef(false);

  const list = usePagedList(ep.indexingFiles(projectId), { size: 50 });
  const { filters, setFilter } = list;
  const statusFilter = filters.status || 'all';

  const loadSummary = useCallback(async () => {
    const res = await api.get(ep.indexingSummary(projectId));
    if (!res.success) return;
    setSummary(res.response);
    const { counts, queue, job } = res.response;
    activeRef.current =
      counts.pending + counts.extracting + counts.missing > 0 ||
      (queue.depth ?? 0) > 0 ||
      (job && (job.status === 'queued' || job.status === 'running'));
  }, [projectId]);

  const loadHealth = useCallback(async () => {
    const res = await api.get(ep.indexingHealth(projectId));
    if (res.success) setHealth(res.response);
  }, [projectId]);

  // The last few jobs (settled ones — the active job has its own card).
  const loadJobs = useCallback(async () => {
    const res = await api.get(ep.indexingJobs(projectId), { params: { page: 1, size: 5 } });
    if (res.success) setRecentJobs((res.response.items || []).filter((j) => j.status !== 'queued' && j.status !== 'running'));
  }, [projectId]);

  const loadDoctor = useCallback(async () => {
    setDoctorLoading(true);
    const res = await api.get(ep.indexingDoctor(projectId));
    setDoctorLoading(false);
    if (res.success) setDoctor(res.response);
    else toast.error(res.message);
  }, [projectId]);

  useEffect(() => {
    loadSummary();
    loadHealth();
    loadJobs();
  }, [loadSummary, loadHealth, loadJobs]);

  // When the active job leaves the card, it belongs in the history.
  const activeJobId = summary?.job && (summary.job.status === 'queued' || summary.job.status === 'running') ? summary.job.id : null;
  const prevActiveRef = useRef(null);
  useEffect(() => {
    if (prevActiveRef.current && !activeJobId) loadJobs();
    prevActiveRef.current = activeJobId;
  }, [activeJobId, loadJobs]);

  // One steady interval; each tick decides for itself (hidden tab or an idle
  // pipeline → no request at all). Includes the running job, whose tasks may
  // create no pending rows (embeddings) — without it the card would freeze.
  useEffect(() => {
    const id = setInterval(() => {
      if (document.hidden || !activeRef.current) return;
      loadSummary();
      list.reload({ silent: true });
    }, POLL_MS);
    return () => clearInterval(id);
  }, [loadSummary, list.reload]); // eslint-disable-line react-hooks/exhaustive-deps

  const afterAction = () => {
    loadSummary();
    list.reload({ silent: true });
    if (doctor) loadDoctor();
    loadJobs();
  };

  const retry = async (body, key) => {
    setBusy(key);
    const res = await api.post(ep.indexingRetry(projectId), { data: body });
    setBusy(null);
    if (!res.success) { toast.error(res.message); return; }
    if (res.response.capped) toast(t('indexing.toasts.queuedCapped', { count: res.response.queued }));
    else toast.success(t('indexing.toasts.queued', { count: res.response.queued }));
    setSelected(new Set());
    afterAction();
  };

  const rebuild = async (scope, force) => {
    setBusy('rebuild');
    const res = await api.post(ep.indexingRebuild(projectId), { data: { scope, force } });
    setBusy(null);
    if (!res.success) { toast.error(res.message); return; }
    toast.success(t('indexing.toasts.rebuildStarted', { count: res.response.total }));
    afterAction();
  };

  const cancelJob = async () => {
    const job = summary?.job;
    if (!job) return;
    const res = await api.post(ep.indexingJobCancel(projectId, job.id));
    if (!res.success) { toast.error(res.message); return; }
    toast(t('indexing.toasts.cancelled'));
    afterAction();
  };

  const cleanup = async () => {
    setBusy('cleanup');
    const res = await api.post(ep.indexingCleanup(projectId), { data: { what: 'orphan_vectors' } });
    setBusy(null);
    if (!res.success) { toast.error(res.message); return; }
    toast.success(t('indexing.toasts.cleaned', { count: res.response.removed }));
    afterAction();
  };

  const onFix = (check) => {
    if (check.fix.startsWith('cleanup:')) setConfirm({ kind: 'cleanup', count: check.count });
    // The check's count describes the scope only when the check IS the
    // scope (`failed` → failed); a missing collection's "0 points" is not
    // how many files a forced embeddings rebuild will queue.
    else setConfirm({ kind: 'rebuild', scope: check.fix, force: Boolean(check.force), count: check.key === check.fix ? check.count : null });
  };

  const chipFilter = (value) => setFilter('status', statusFilter === value ? 'all' : value);

  const counts = summary?.counts;
  const embed = summary?.embed_counts;
  const queue = summary?.queue;
  const job = summary?.job;
  const items = list.items;
  const allSelected = items.length > 0 && items.every((i) => selected.has(i.file_id));
  const selectedRetryable = useMemo(() => items.filter((i) => selected.has(i.file_id)), [items, selected]);

  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(items.map((i) => i.file_id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  return (
    <div className="space-y-4">
      {/* Health + refresh line */}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <HealthStrip health={health} t={t} />
        <span className="text-xs text-muted-foreground">
          {summary?.generated_at && t('indexing.updatedAt', { time: moment(summary.generated_at).format('HH:mm:ss') })}
          {activeRef.current && ` · ${t('indexing.refreshing')}`}
        </span>
      </div>

      <JobCard job={job} workerOk={health?.worker?.ok} onCancel={cancelJob} t={t} />
      <RecentJobs jobs={recentJobs.filter((j) => j.id !== job?.id)} t={t} />

      {/* Summary chips — click to filter the table. */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map(({ value, chip }) => (
          <button
            key={value}
            type="button"
            onClick={() => chipFilter(value)}
            data-testid={`chip-${value}`}
            aria-pressed={statusFilter === value}
            className={cn(
              'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm transition cursor-pointer',
              chip,
              statusFilter === value ? 'ring-2 ring-offset-1 ring-blue-300 dark:ring-blue-700' : 'hover:opacity-80',
            )}
          >
            <span className="font-semibold">{counts ? counts[value] : '–'}</span>
            {t(`indexing.status.${value}`)}
          </button>
        ))}
        <span
          className={cn(
            'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-sm',
            queue?.reachable ? 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300 border-sky-200 dark:border-sky-800' : 'bg-muted/50 text-muted-foreground border-border',
          )}
          title={queue?.reachable ? undefined : t('indexing.chips.queueOfflineHint')}
        >
          <RefreshCw className={cn('h-3.5 w-3.5', (queue?.depth ?? 0) > 0 && 'animate-spin')} />
          {queue?.reachable ? t('indexing.chips.queue', { count: queue.depth }) : t('indexing.chips.queueOffline')}
        </span>
      </div>

      {embed && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>{t('indexing.embedTitle')}</span>
          {EMBED_STATUSES.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter('embed_status', filters.embed_status === key ? 'all' : key)}
              className={cn(
                'rounded-full border px-2 py-0.5 cursor-pointer',
                filters.embed_status === key ? 'ring-2 ring-blue-300 dark:ring-blue-700 bg-card' : 'bg-muted/50 hover:bg-card',
              )}
            >
              <span className="font-semibold">{embed[key] ?? 0}</span> {t(`indexing.embed.${key}`)}
            </button>
          ))}
        </div>
      )}

      {/* Doctor */}
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={loadDoctor} disabled={doctorLoading} className="gap-2">
          {doctorLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Stethoscope className="h-4 w-4" />}
          {doctor ? t('indexing.doctor.rerun') : t('indexing.doctor.run')}
        </Button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" className="gap-2" disabled={Boolean(job && (job.status === 'queued' || job.status === 'running'))} data-testid="rebuild-menu">
              <RotateCw className="h-4 w-4" /> {t('indexing.rebuild.button')} <ChevronDown className="h-3 w-3" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-64">
            {SCOPES.map((scope) => (
              <DropdownMenuItem key={scope} onClick={() => setConfirm({ kind: 'rebuild', scope, force: false, count: doctor?.checks?.find((c) => c.key === scope)?.count })} className="flex flex-col items-start gap-0.5">
                <span className="font-medium">{t(`indexing.scopes.${scope}`)}</span>
                <span className="text-xs text-muted-foreground">{t(`indexing.scopeHints.${scope}`)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => setConfirm({ kind: 'rebuild', scope: 'all', force: true, count: null })} className="text-amber-700 dark:text-amber-300">
              {t('indexing.rebuild.allForce')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      <DoctorPanel report={doctor} onFix={onFix} busy={busy !== null} t={t} />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-56 max-w-md">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={list.searchInput} onChange={(e) => list.setSearchInput(e.target.value)} placeholder={t('indexing.searchPlaceholder')} className="pl-9" />
        </div>
        <Select value={statusFilter} onValueChange={(value) => setFilter('status', value)}>
          <SelectTrigger className="w-44" aria-label={t('indexing.table.status')}><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{t('indexing.filterAll')}</SelectItem>
            {STATUSES.map(({ value }) => (
              <SelectItem key={value} value={value}>{t(`indexing.status.${value}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={`${filters.sort || 'updated'}:${filters.order || 'desc'}`} onValueChange={(v) => { const [sort, order] = v.split(':'); setFilter('sort', sort); setFilter('order', order); }}>
          <SelectTrigger className="w-44" aria-label={t('indexing.sort.label')}><SelectValue /></SelectTrigger>
          <SelectContent>
            {['updated:desc', 'updated:asc', 'name:asc', 'name:desc', 'status:asc', 'indexed:desc'].map((v) => (
              <SelectItem key={v} value={v}>{t(`indexing.sort.${v.replace(':', '_')}`)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {list.hasFilters && (
          <Button variant="ghost" size="sm" onClick={list.clearFilters}>{t('list.clearFilters')}</Button>
        )}
        <div className="ml-auto flex items-center gap-2">
          {selectedRetryable.length > 0 && (
            <Button variant="outline" size="sm" disabled={busy !== null} onClick={() => retry({ file_ids: selectedRetryable.map((i) => i.file_id) }, 'selected')} className="gap-2">
              {busy === 'selected' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
              {t('indexing.retrySelected', { count: selectedRetryable.length })}
            </Button>
          )}
          {counts?.failed > 0 && (
            <Button variant="outline" size="sm" disabled={busy !== null} onClick={() => setConfirm({ kind: 'retryAll', count: counts.failed })} className="gap-2 text-red-600 dark:text-red-400 border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20">
              {busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />}
              {t('indexing.retryAll', { count: counts.failed })}
            </Button>
          )}
        </div>
      </div>

      {/* Table */}
      {list.loading && items.length === 0 ? (
        <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 text-blue-500 dark:text-blue-400 animate-spin" /></div>
      ) : items.length > 0 ? (
        <>
          <div className="w-full border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-8"><Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label={t('indexing.selectAll')} /></TableHead>
                  <TableHead>{t('indexing.table.name')}</TableHead>
                  <TableHead className="w-32">{t('indexing.table.status')}</TableHead>
                  <TableHead>{t('indexing.table.details')}</TableHead>
                  <TableHead className="w-40 text-center">{t('indexing.table.updated')}</TableHead>
                  <TableHead className="w-24 text-center">{t('indexing.table.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={`${item.file_id}-${item.status}`} className="cursor-pointer" onClick={() => setDrawerRow(item)} data-row-id={item.file_id}>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selected.has(item.file_id)} onCheckedChange={() => toggleOne(item.file_id)} aria-label={item.name || ''} />
                    </TableCell>
                    <TableCell className="font-medium max-w-64 truncate" title={item.name || ''}>{item.name}</TableCell>
                    <TableCell><StatusChip status={item.status} t={t} /></TableCell>
                    <TableCell className="max-w-96">
                      {item.error ? (
                        <span className="block truncate font-mono text-xs text-red-600 dark:text-red-400" title={item.error}>{item.error}</span>
                      ) : item.embed_error ? (
                        <span className="block truncate font-mono text-xs text-amber-600 dark:text-amber-400" title={item.embed_error}>{t('indexing.embedErrorPrefix')} {item.embed_error}</span>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {item.extractor && `${item.extractor} · `}{item.lang || ''}{item.truncated && ` · ${t('indexing.truncated')}`}
                          {item.attempts > 1 && ` · ${t('indexing.attempts', { count: item.attempts })}`}
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="text-center text-sm text-muted-foreground">{fmt(item.updated_at)}</TableCell>
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      {RETRYABLE.has(item.status) && (
                        <Button variant="ghost" size="sm" disabled={busy !== null} onClick={() => retry({ file_ids: [item.file_id] }, item.file_id)} title={t('indexing.retry')} className="gap-1 h-8">
                          {busy === item.file_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RotateCw className="h-3.5 w-3.5" />}
                          {t('indexing.retry')}
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          <ListPagination list={list} />
        </>
      ) : (
        <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
          <ScanSearch className="w-12 h-12 mb-3 text-muted-foreground/60" />
          <p className="text-lg font-medium">{list.hasFilters ? t('indexing.empty.noMatch') : t('indexing.empty.title')}</p>
          <p className="text-sm text-muted-foreground">{list.hasFilters ? t('indexing.empty.noMatchHint') : t('indexing.empty.description')}</p>
          {list.hasFilters && <Button variant="ghost" size="sm" className="mt-2" onClick={list.clearFilters}>{t('list.clearFilters')}</Button>}
        </div>
      )}

      <RowDrawer
        row={drawerRow}
        onClose={() => setDrawerRow(null)}
        onRetry={(row, force) => { setDrawerRow(null); retry({ file_ids: [row.file_id], force }, row.file_id); }}
        t={t}
      />

      <ConfirmDialog
        open={Boolean(confirm)}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={
          confirm?.kind === 'rebuild' ? t('indexing.confirm.rebuildTitle', { scope: t(`indexing.scopes.${confirm.scope}`) })
            : confirm?.kind === 'cleanup' ? t('indexing.confirm.cleanupTitle')
              : t('indexing.confirm.retryAllTitle', { count: confirm?.count ?? 0 })
        }
        description={
          confirm?.kind === 'rebuild'
            ? [
              confirm.count != null ? t('indexing.confirm.rebuildCount', { count: confirm.count }) : t(`indexing.scopeHints.${confirm.scope}`),
              // Force means different work per scope: the embeddings scope never touches Tika.
              confirm.force ? t(confirm.scope === 'embeddings' ? 'indexing.confirm.forceWarningEmbeddings' : 'indexing.confirm.forceWarning') : null,
            ].filter(Boolean).join(' ')
            : confirm?.kind === 'cleanup' ? t('indexing.confirm.cleanupBody', { count: confirm.count ?? 0 })
              : t('indexing.confirm.retryAllBody')
        }
        confirmLabel={confirm?.kind === 'cleanup' ? t('indexing.doctor.cleanup') : confirm?.kind === 'rebuild' ? t('indexing.rebuild.start') : t('indexing.retry')}
        destructive={confirm?.kind === 'cleanup' || Boolean(confirm?.force)}
        onConfirm={async () => {
          if (confirm.kind === 'rebuild') await rebuild(confirm.scope, confirm.force);
          else if (confirm.kind === 'cleanup') await cleanup();
          else await retry({ scope: 'failed' }, 'all');
        }}
      >
        {confirm?.kind === 'rebuild' && FORCEABLE.has(confirm.scope) && (
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={Boolean(confirm.force)} onCheckedChange={(v) => setConfirm({ ...confirm, force: Boolean(v) })} />
            {t('indexing.confirm.forceLabel')}
          </label>
        )}
      </ConfirmDialog>
    </div>
  );
};
